import React, { useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  Collapse,
  useTheme,
  Grid,
  TextField,
} from "@mui/material";
import { getFormSectionStyles } from "../formSectionStyles";
import DynamicServiceForm from "../DynamicServiceForm";
import { StepDefinition, getStepDefinitionMap, getStepDefinitions } from "../stepDefinitions";
import { PROCESSING_REGISTRY } from "./processingRegistry";


const PROCESSING_STEPS = getStepDefinitions("processing");
const PROCESSING_STEP_MAP = getStepDefinitionMap("processing");
const SERVICE_ORDER = PROCESSING_STEPS.map((s) => s.id);

const GENERAL_DEFINITION: StepDefinition = {
  id: "general",
  service_description: "General Asset Processing Settings",
  service_flag: [],
  service_category: "processing",
  label: "General",
  order: -1,
  renderer: "dynamic",
  stepFlags: [],
  fields: [
    {
      key: "imageAspectRatio",
      label: "Image Aspect Ratio",
      type: "select",
      options: [
        { label: "4:3", value: "4:3" },
        { label: "16:9", value: "16:9" },
      ],
    },
    {
      key: "cameraDirection",
      label: "Camera Direction",
      type: "select",
      options: [
        { label: "Clockwise", value: "cw" },
        { label: "Counter-Clockwise", value: "ccw" },
      ],
    },
    {
      key: "unitHeight",
      label: "Leg Height in Inches",
      type: "number",
    },
  ],
};

interface AssetProcessingFormProps {
  config: any;
  handleChange: (path: string, value: any) => void;
}

const AssetProcessingForm: React.FC<AssetProcessingFormProps> = ({
  config,
  handleChange,
}) => {
  const theme = useTheme();
  const styles = getFormSectionStyles(theme);
  const cachedConfigsRef = useRef<Record<string, any>>({});
  const disabledServicesRef = useRef<Set<string>>(new Set());

  const enabledServices = useMemo(() => {
    return SERVICE_ORDER.filter((s) => {
      const key = `assetProcessing_${s}_serviceConfig`;
      return config[key] !== undefined;
    });
  }, [config]);

  /** 🔁 Rebuilds flow steps in the correct order and logic */
  const rebuildFlowSteps = (services: string[]): string[] => {
    const ordered = SERVICE_ORDER.filter((s) => services.includes(s));
    const steps: string[] = [];

    ordered.forEach((service) => {
      const definition = PROCESSING_STEP_MAP[service];
      if (!definition) return;

      definition.stepFlags.forEach((flag) => {
        if (flag.excludeIfEnabled?.some((dep) => services.includes(dep))) return;
        if (!steps.includes(flag.key)) steps.push(flag.key);
      });
    });

    return steps;
  };

  /** 🔘 Enable/disable a service */
  const toggleService = (service: string, enabled: boolean) => {
    const serviceKey = `assetProcessing_${service}_serviceConfig`;
    const definition = PROCESSING_STEP_MAP[service];

    // Update configs
    if (enabled) {
      disabledServicesRef.current.delete(service);
      const seed = PROCESSING_REGISTRY[service]?.defaults ?? definition?.defaults ?? {};
      const restored =
        cachedConfigsRef.current[service] ||
        structuredClone(seed);
      handleChange(serviceKey, restored);
      cachedConfigsRef.current[service] = restored;
    } else {
      disabledServicesRef.current.add(service);
      cachedConfigsRef.current[service] = config[serviceKey];
      handleChange(serviceKey, undefined);
    }

    // Determine new enabled list
    const updatedEnabled = enabled
      ? [...enabledServices, service]
      : enabledServices.filter((s) => s !== service);

    // ✅ Rebuild flowControl based on updated services
    const newFlow = rebuildFlowSteps(updatedEnabled);
    handleChange("flowControl_assetProcessing_steps", newFlow);
  };

  return (
    <Paper
      sx={styles.paper}
    >
      <Typography
        variant="h6"
        sx={styles.title}
      >
        Asset Processing Services
      </Typography>

      {/* General settings (always visible) */}
      <Box sx={{ mb: 3 }}>
        <DynamicServiceForm
          definition={GENERAL_DEFINITION}
          config={config.general || {}}
          onChange={(field: string, value: any) => {
            handleChange(`general.${field}`, value);
          }}
        />
      </Box>

      {/* Checkbox row */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {SERVICE_ORDER.map((service) => {
          const enabled = enabledServices.includes(service);
          const definition = PROCESSING_STEP_MAP[service];
          return (
            <Grid item key={service}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enabled}
                    onChange={(e) => toggleService(service, e.target.checked)}
                    sx={styles.checkbox}
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 500 }}>
                    {definition?.label ||
                      service
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (s) => s.toUpperCase())}
                  </Typography>
                }
              />
            </Grid>
          );
        })}
      </Grid>

      {/* Expanded mini-forms */}
      {SERVICE_ORDER.map((service) => {
        const definition = PROCESSING_STEP_MAP[service];
        const serviceKey = `assetProcessing_${service}_serviceConfig`;
        const enabled = enabledServices.includes(service);
        const registryEntry = PROCESSING_REGISTRY[service];

        const handleFieldChange = (field: string, value: any) => {
          if (disabledServicesRef.current.has(service)) return;
          handleChange(`${serviceKey}.${field}`, value);
        };

        let body: React.ReactNode = null;
        if (registryEntry) {
          const FormComponent = registryEntry.Component;
          body = (
            <FormComponent
              config={config[serviceKey] || {}}
              onChange={handleFieldChange}
            />
          );
        } else if (definition?.renderer === "dynamic") {
          body = (
            <DynamicServiceForm
              definition={definition}
              config={config[serviceKey] || {}}
              onChange={handleFieldChange}
            />
          );
        }

        return (
          <Collapse key={service} in={enabled} unmountOnExit>
            <Box
              sx={styles.collapsibleRail}
            >
              {body}
            </Box>
          </Collapse>
        );
      })}

      {/* Flow Control Steps */}
      <Typography
        variant="h6"
        sx={{ ...styles.title, mt: 3, mb: 1 }}
      >
        Flow Control Steps
      </Typography>
      <TextField
        fullWidth
        multiline
        minRows={3}
        value={
          Array.isArray(config.flowControl_assetProcessing_steps)
            ? config.flowControl_assetProcessing_steps.join("\n")
            : config.flowControl_assetProcessing_steps || ""
        }
        onChange={(e) =>
          handleChange(
            "flowControl_assetProcessing_steps",
            e.target.value
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean)
              .join(",")
          )
        }
        sx={{
          fontFamily: "monospace",
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: theme.palette.divider },
            "&:hover fieldset": { borderColor: styles.accent },
            "&.Mui-focused fieldset": { borderColor: styles.accent },
          },
        }}
      />
    </Paper>
  );
};

export default AssetProcessingForm;
